// Where the rail sits, and where it sits again after the rows under it have
// been replaced (SPEC-redesign §5.3, #183, #190, #209; SPEC-viewfinder §2.3).
//
// Split out of `axis-view.ts`, which owns the rebuild: this file owns the
// scroll around it. Every function here reads rendered boxes, so it is the
// half that cannot be unit-tested without a browser — `e2e/rail-anchor.spec.ts`
// is what holds it.
//
// The rule, in one sentence and in two halves. Straight at „teraz" the band is
// the whole answer: a poll, a mute or an event crossing the hour may not slide
// it down the rail. Scrolled away from it — or reading a list ordered by
// distance, where there is no „teraz" to be at — what must stand still is the
// rows being read.
import { FOLD_ROW, RUN_ROW } from './rows.ts';

/** The kreska's own row in the list (§5.3). Sticky, and the one row nobody rebuilds. */
export const KRESKA_ROW = 'row--kreska';

/**
 * Put the rail where the target says. One assignment, and that is all.
 *
 * The target stays a function because the caller computes it from a boundary
 * that the rebuild has just moved: `reserveTail` settles the list's height
 * first, and the boundary this is measured against moved with the rows that
 * were replaced.
 */
export const setScroll = (rail: HTMLElement, target: () => number): void => {
  rail.scrollTop = target();
};

/** The row an item id stands for, or `null` when the list has no row for it. */
export const rowFor = (list: HTMLElement, id: string): HTMLElement | null =>
  list.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"]`);

/** The kreska's own row in the list. */
const kreskaSlot = (list: HTMLElement) => list.querySelector<HTMLElement>(`.${KRESKA_ROW}`);

/** How much list stands above the band. Static rows, so honest at any scroll. */
function heightAbove(list: HTMLElement, kreska: HTMLElement): number {
  let above = 0;
  for (const row of list.children) {
    if (row === kreska) break;
    above += row.getBoundingClientRect().height;
  }
  return above;
}

/**
 * Paper under the last row, so that the kreska can always be brought to the
 * rail's top edge.
 *
 * Without it, „the rail opens on teraz" fails quietly on a quiet day: with less
 * ahead of „teraz" than the rail is tall the scroll runs out before the band
 * gets there, the band stays wherever the content leaves it and the past sits
 * above the fold. The prototype reserves exactly this — an empty tail block as
 * the last child of its scroller, 253 px at 390×844 and 672 px at 1440, sized
 * so its own boundary case (catalogue 13, „nic nie jedzie", 44 px under the
 * band) still pins.
 *
 * Here it is padding on the list rather than a row: a row would be another
 * entry in an `<ol>` for a screen reader to walk to and find empty.
 */
export function reserveTail(list: HTMLElement, kreska: HTMLElement): void {
  const rail = list.parentElement;
  if (!rail) return;
  // The rows' own height, walked rather than worked out from `list.scrollHeight`
  // minus the reservation already written. Those two can be readings of two
  // different moments: `padding-bottom` is in the inline style the instant it is
  // set, but when it is set while a CSS transition applies to it, `scrollHeight`
  // reports the ANIMATED value, which advances only when the rendering steps run
  // — a forced layout does not move it. `.axis` no longer transitions
  // (`src/axis/axis.css` says why), and the walk means this does not have to
  // trust that: a row's box carries no reservation, so there is nothing to take
  // back off it (#228).
  let above = 0;
  let rows = 0;
  let passed = false;
  for (const row of list.children) {
    const height = row.getBoundingClientRect().height;
    rows += height;
    if (row === kreska) passed = true;
    else if (!passed) above += height;
  }
  const reserved = Number.parseFloat(list.style.paddingBottom) || 0;
  // The band reaches the top when the rail can be scrolled at least as far as
  // the band's own place in the list.
  const want = Math.max(0, Math.ceil(above + rail.clientHeight - rows));
  // Written only when it changes: a steady rail then costs no style write at
  // all, and none of the five rebuilds in the first second moves the paper.
  if (want !== reserved) list.style.paddingBottom = `${want}px`;
}

/**
 * Where a row sits in the rail's own scroll, from the rendered boxes rather
 * than `offsetTop`: that is measured against whatever ancestor happens to be
 * positioned, and the desktop column is not the phone column — the same
 * arithmetic was 273 px out there.
 */
export const scrollTopOf = (rail: HTMLElement, row: HTMLElement) =>
  rail.scrollTop + (row.getBoundingClientRect().top - rail.getBoundingClientRect().top);

/**
 * Where the band sits in the CONTENT, in the rail's own scroll coordinates.
 *
 * Never read off the band's own box. It is `position: sticky`, so while it is
 * pinned that box reports where it is DRAWN — the rail's top edge, which is
 * `rail.scrollTop` exactly — and every comparison against it becomes a number
 * against itself. That cost two behaviours their meaning: `nowTarget` returned
 * the scroll the rail already had, so moving the axis's hour BACKWARDS could
 * not re-place the rail, and `scrollRowIntoView`'s „is this row behind the
 * band" test could never be true, so an opened row was left under it (review 1
 * and 2). The rows above the band are static, and their heights are honest
 * whatever the scroll is doing.
 *
 * From the list's own top rather than the rail's: the rail also holds the
 * heading „Oś czasu".
 */
const boundaryOf = (rail: HTMLElement, list: HTMLElement, kreska: HTMLElement) =>
  scrollTopOf(rail, list) + heightAbove(list, kreska);

/**
 * Within this many pixels of the boundary the band counts as standing on the
 * rail's top edge. One device pixel, because the boundary is a sum of
 * fractional row heights and a scroll is stored rounded to the device's.
 *
 * What the tests hold is the MEANING and not this number: a few pixels either
 * way changes nothing a resident can see, and the suite is green at 5 (review
 * 3). It bites in the hundreds, where „a fraction short of the boundary" would
 * start swallowing a deliberate scroll into the past.
 */
const PIN_EPS = 1;

/**
 * What the resident was looking at when a rebuild started, in terms that
 * survive the rows being replaced (#183).
 *
 * `gap` is the scroll measured from the band: `0` means the band is standing on
 * the rail's top edge and the resident is looking straight at „teraz".
 * `holds` are the rows they can see and name, nearest the top of what they are
 * reading first — a list and not one row, because the rebuild they are about to
 * meet is very often the one that takes that row away (muting the voice of the
 * row you are reading dissolves it into a thin line). The next one down then
 * answers, and the whole screen still stands still.
 */
type Hold = { key: string; off: number };
/**
 * `fallback` is what to do when none of the held rows survived the rebuild. In
 * time order the band is the landmark every rebuild keeps, so the answer
 * degrades to „the same place on the axis". In distance order there is no
 * „teraz" boundary to measure from — the kreska is the list's head — so it
 * degrades to the top, which is §2.3's rule.
 */
export type Anchor = { gap: number; holds: Hold[]; fallback: 'band' | 'top' };

/**
 * A row's name across a rebuild. An item keeps its `data-id`; the fold is the
 * one row without one that a resident presses, and there is only ever one.
 * Everything else — a tick, a note, a gap, a mute run — stands for rows rather
 * than being one, so it is skipped rather than anchored on.
 */
const rowKey = (row: HTMLElement): string | null =>
  row.dataset.id ? `id:${row.dataset.id}` : row.classList.contains(FOLD_ROW) ? FOLD_ROW : null;

const rowByKey = (list: HTMLElement, key: string): HTMLElement | null =>
  key === FOLD_ROW
    ? list.querySelector<HTMLElement>(`.${FOLD_ROW}`)
    : list.querySelector<HTMLElement>(`[data-id="${CSS.escape(key.slice(3))}"]`);

/**
 * The rows on the screen the resident could name, in the order they should be
 * tried after the rebuild: from where they are reading, away from the band.
 *
 * A row that stands for other rows — a tick, a note, a gap, a mute run — is
 * skipped rather than held: it has no name that survives. Skipping costs
 * nothing, because what is kept is the next row's OFFSET from the rail's top
 * edge, and everything above it rides along.
 */
function holdsOf(rail: HTMLElement, list: HTMLElement, kreska: HTMLElement, ahead: boolean): Hold[] {
  const railBox = rail.getBoundingClientRect();
  const rows = [...list.children].filter((row): row is HTMLElement => row instanceof HTMLElement);
  const at = rows.indexOf(kreska);
  // Above the band when the resident has scrolled back into the past, below it
  // when they are reading ahead: either way, the rows they are looking at.
  const side = ahead ? rows.slice(at + 1) : rows.slice(0, Math.max(at, 0));
  // Reading ahead, the screen starts under the band: it covers the rail's first
  // `height` pixels for as long as it is pinned there, and a row behind it is
  // not one anybody is reading.
  const from = ahead ? kreska.getBoundingClientRect().height : 0;
  const holds: Hold[] = [];
  for (const row of side) {
    const box = row.getBoundingClientRect();
    // Only what is on the screen, and below the band where it is pinned: a row
    // hidden behind the band is not what anyone is reading.
    if (box.bottom <= railBox.top + from + PIN_EPS || box.top >= railBox.bottom) continue;
    const key = rowKey(row);
    if (key) holds.push({ key, off: scrollTopOf(rail, row) - rail.scrollTop });
  }
  return holds;
}

/**
 * What to hold still through the rebuild that is about to happen.
 *
 * Straight at „teraz", and the band is the whole answer: a poll, a mute or an
 * event crossing the hour may not slide it down the rail. Scrolled away from
 * it — back into the past or on into the evening — and what must stand still is
 * the rows being read, because holding the band there would move them.
 */
export function anchorOf(rail: HTMLElement, list: HTMLElement, kreska: HTMLElement, byTime: boolean): Anchor {
  const gap = rail.scrollTop - boundaryOf(rail, list, kreska);
  // By distance the „teraz" boundary means nothing (§2.3), so the pinned case
  // does not apply and the rule is the second one alone: the first row the
  // resident can see and name keeps its height on the screen.
  if (!byTime) return { gap, holds: holdsOf(rail, list, kreska, true), fallback: 'top' };
  // Pinned is pinned: a scroll a fraction of a pixel short of the boundary is
  // the band held at the rail's edge, and carrying that fraction across the
  // rebuild would leave it a pixel down — rebuild after rebuild.
  if (Math.abs(gap) <= PIN_EPS) return { gap: 0, holds: [], fallback: 'band' };
  return { gap, holds: holdsOf(rail, list, kreska, gap > 0), fallback: 'band' };
}

/**
 * The scroll that puts the anchor back where it was.
 *
 * The first held row that is still there decides, and that is the rule: a mute
 * dissolves the row being read into a thin line, and the next row down is then
 * what the screen is judged by. With none of them left — every voice on the
 * screen silenced at once — the distance to the band is the honest fallback,
 * the one landmark every rebuild keeps: it degrades to „the same place on the
 * axis", never to the top.
 *
 * Reading ahead, the answer is never allowed below the boundary: the band was
 * standing on the rail's top edge and it stays there, whatever the rows under
 * it did.
 */
export function scrollForAnchor(
  rail: HTMLElement,
  list: HTMLElement,
  kreska: HTMLElement,
  anchor: Anchor,
): number {
  const boundary = boundaryOf(rail, list, kreska);
  for (const hold of anchor.holds) {
    const row = rowByKey(list, hold.key);
    if (row) {
      const top = scrollTopOf(rail, row) - hold.off;
      return anchor.gap > 0 ? Math.max(boundary, top) : Math.max(0, top);
    }
  }
  return anchor.fallback === 'top' ? 0 : Math.max(0, boundary + anchor.gap);
}

/**
 * How far the rail has to be scrolled for the kreska to sit at its top edge.
 *
 * The kreska is the „teraz" boundary itself — the row the past ends at and the
 * future begins under — so putting it at the top puts „teraz" at the top, and
 * the first future row directly under it rather than behind it.
 */
function nowTarget(list: HTMLElement): number | null {
  const slot = kreskaSlot(list);
  const rail = list.parentElement;
  // Not until there is something to read: the first snapshot is usually nothing
  // but „czę się…", and positioning against that would leave the resident
  // at the top of eleven days of history.
  //
  // A mute run counts as something to read. It carries no `data-id` — it stands
  // for the rows it replaced — and with every voice silenced the rail holds
  // nothing else, so an item-only test left that resident looking at the band
  // 44 px down a rail it had never placed. A run exists only once real items
  // have arrived to be collapsed, so the guard still holds for a starting app.
  if (!rail || !slot || !list.querySelector(`[data-id], .${RUN_ROW}`)) return null;
  return boundaryOf(rail, list, slot);
}

/**
 * Puts „teraz" at the top of the rail: the first row at or after it sits
 * directly under the kreska, and the past is above, reached by scrolling up.
 * A resident opens the app to find out what is happening now, not to read what
 * a source published eleven days ago.
 *
 * Once per render for as long as the app is loading, and the last one is what
 * the resident sees. The four feeds answer one at a time: a scroll set against
 * a list that is still growing is clamped to however far that shorter list
 * could reach, and the rows landing a moment later push „teraz" back off the
 * screen. So `render` calls this again on every snapshot until the load window
 * closes (`LOAD_MS`), and the target is measured afresh each time.
 *
 * Set on the rail itself rather than through `scrollIntoView`, which walks up
 * to whichever ancestor it finds scrollable and can leave the rail exactly
 * where it was while the page moves underneath it.
 */
export function placeAtNow(list: HTMLElement): void {
  const box = list.parentElement;
  const target = nowTarget(list);
  if (!box || target == null) return;
  box.scrollTop = target;
}

/**
 * Brings an opened row onto the screen, and only when it is not already there.
 *
 * Set on the rail rather than through `scrollIntoView`: that walks up to
 * whichever ancestor it finds scrollable, and here it moves the page while
 * leaving the rail exactly where it was.
 *
 * The kreska is a row of this list and it is pinned to the rail's top edge for
 * as long as the resident is looking into the future, so it stands over the
 * rail's first `height` pixels there — an opened future row is aligned under
 * it, not under the rail's own top, or its first line would be behind the band.
 * A row above the kreska has nothing over it: at any scroll that reaches such a
 * row the kreska has let go and travelled down with the content, so that row
 * aligns with the rail's top exactly as it used to. The height is read here and
 * not remembered: the grip, the dial line and the geolocation note each change
 * it.
 */
export function scrollRowIntoView(list: HTMLElement, id: string): void {
  const opened = rowFor(list, id);
  const rail = list.parentElement;
  if (!opened || !rail) return;
  const slot = kreskaSlot(list);
  // `Infinity` with no band to clear: every row then aligns with the rail's own
  // top, which is what this did before the band moved into the list.
  const boundary = slot ? boundaryOf(rail, list, slot) : Number.POSITIVE_INFINITY;
  const band = slot?.getBoundingClientRect().height ?? 0;
  const top = scrollTopOf(rail, opened);
  const bottom = top + opened.offsetHeight;
  const seenFrom = rail.scrollTop;
  const seenTo = seenFrom + rail.clientHeight;
  // Already fully in view: leave the rail alone, as `block: 'nearest'` would —
  // and what „in view" means starts below the band wherever the band is pinned.
  // `>=`, because pinned is the equality case: at the boundary itself the band
  // is already standing on the rail's top edge (review 1).
  const covered = seenFrom >= boundary ? band : 0;
  if (top >= seenFrom + covered && bottom <= seenTo) return;
  rail.scrollTop = top > boundary ? Math.max(boundary, top - band) : top;
}

/**
 * The new rows, around the kreska rather than in place of it.
 *
 * `replaceChildren` would take the kreska out of the document too, and an
 * element that leaves the document loses the focus it holds — the summary
 * button under the resident's finger would be blurred five times in the first
 * second. Leaving that one row attached also leaves its live region alone (#72)
 * and keeps it the same element the whole way through.
 *
 * The paper under the last row is re-measured here and not later, because it is
 * what decides how far the rail may be scrolled at all.
 */
export function replaceRows(
  list: HTMLElement,
  kreska: HTMLElement,
  before: DocumentFragment,
  after: DocumentFragment,
): void {
  // From the end down: `children` is live, and walking it forwards while
  // removing from it skips every second row.
  for (let i = list.children.length - 1; i >= 0; i--) {
    const child = list.children[i];
    if (child && child !== kreska) child.remove();
  }
  list.insertBefore(before, kreska);
  list.append(after);
  reserveTail(list, kreska);
}
